import {
    Shield,
    Users,
    Crown,
    Wallet,
    Heart,
    GraduationCap,
    TrendingUp,
    TrendingDown,
    Minus,
    Car,
    Building,
    Leaf,
    MapPin,
    Trophy,
    type LucideIcon,
} from 'lucide-react'

export interface KpiItem {
    name: string
    value: number
    target: number
    unit: string
    status: string
    tranding: string
}

export interface DepartmentKpi {
    name: string
    description: string
    icon: LucideIcon
    color: string
    bgColor: string
    progressBarColor: string
    kpis: KpiItem[]
    source: string
}

export const calculatePerformance = (value: number, target: number): number => {
    return Math.min(Math.round((value / target) * 100), 100)
}

export const getTrendIndicator = (value: number, target: number) => {
    const percentage = (value / target) * 100

    if (percentage >= 100) {
        return { icon: TrendingUp, color: 'success', text: 'Exceeding' }
    }
    if (percentage >= 80) {
        return { icon: TrendingUp, color: 'success', text: 'On Track' }
    }
    if (percentage >= 50) {
        return { icon: Minus, color: 'warning', text: 'Behind' }
    }
    return { icon: TrendingDown, color: 'danger', text: 'Critical' }
}

export const getStatusColorFromText = (status: string): string => {
    return status === 'Good' ? 'primary' : 'success'
}

export const DEPARTMENTS: DepartmentKpi[] = [
    {
        name: 'Premier',
        description: 'Strategic leadership and governance coordination',
        icon: Crown,
        color: 'info',
        bgColor: 'bg-light-info',
        progressBarColor: 'info',
        kpis: [
            { name: 'Strategic plans implemented on schedule', value: 87, target: 85, unit: '%', status: 'Excellent', tranding: 'up' },
            { name: 'Policy development milestones', value: 92, target: 90, status: 'Excellent', tranding: 'up', unit: '%' },
            { name: 'Governance compliance rate', value: 95, target: 95, status: 'Excellent', unit: '%', tranding: '' },
        ],
        source: 'Western Cape Government Department of the Premier, 2024',
    },
    {
        name: 'Treasury',
        description: 'Financial management and budget oversight',
        icon: Wallet,
        color: 'cyan',
        bgColor: 'bg-cyan-1',
        progressBarColor: 'cyan',
        kpis: [
            { name: 'Budget execution rate', value: 2.1, target: 3, status: 'Excellent', unit: '%', tranding: '' },
            { name: 'Financial compliance', value: 89, target: 85, status: 'Excellent', tranding: 'up', unit: '%' },
            { name: 'Procurement efficiency', value: 94, target: 95, status: 'Good', tranding: '', unit: '%' },
        ],
        source: 'Western Cape Provincial Treasury, 2024',
    },
    {
        name: 'Police Oversight and Community Safety',
        description: 'Public safety and crime prevention',
        icon: Shield,
        color: 'info',
        bgColor: 'bg-info-2',
        progressBarColor: 'info',
        kpis: [
            { name: 'Crime prevention initiatives', value: 45, target: 40, status: 'Excellent', unit: 'Programs', tranding: 'up' },
            { name: 'Emergency response time', value: 8.5, target: 7, status: 'Excellent', unit: '%', tranding: 'up' },
            { name: 'Community outreach programs', value: 12, target: 14, status: 'Good', unit: 'days', tranding: 'down' },
        ],
        source: 'Western Cape Government Police Oversight and Community Safety, 2024',
    },
    {
        name: 'Education',
        description: 'Quality education and learner development.',
        icon: GraduationCap,
        color: 'cyan',
        bgColor: 'bg-cyan-2',
        progressBarColor: 'cyan',
        kpis: [
            { name: 'Matric pass rate', value: 82.3, target: 80, status: 'Excellent', unit: '%', tranding: 'up' },
            { name: 'Learner retention rate', value: 78, target: 75, status: 'Good', unit: '%', tranding: 'up' },
            { name: 'Infrastructure development', value: 91, target: 90, status: 'Excellent', unit: '%', tranding: '' },
        ],
        source: 'Western Cape Government Department of Education, 2024',
    },
    {
        name: 'Health & Wellness',
        description: 'Healthcare services and public health',
        icon: Heart,
        color: 'cyan',
        bgColor: 'bg-cyan-3',
        progressBarColor: 'cyan',
        kpis: [
            { name: 'Hospital waiting times', value: 89, target: 90, status: 'Good', unit: '%', tranding: '' },
            { name: 'Vaccination coverage', value: 42, target: 45, status: 'Excellent', unit: 'min', tranding: 'down' },
            { name: 'Disease prevention programs', value: 93, target: 90, status: 'Excellent', unit: '%', tranding: 'up' },
        ],
        source: 'Western Cape Government Department of Health and Wellness, 2024',
    },
    {
        name: 'Social Development',
        description: 'Social services and community support',
        icon: Users,
        color: 'info',
        bgColor: 'bg-info-3',
        progressBarColor: 'info',
        kpis: [
            { name: 'Social grant distribution', value: 125000, target: 120000, status: 'Excellent', unit: 'People', tranding: 'up' },
            { name: 'Child protection services', value: 86, target: 85, status: 'Excellent', unit: '%', tranding: '' },
            { name: 'Community development programs', value: 18, target: 21, status: 'Excellent', unit: 'days', tranding: 'down' },
        ],
        source: 'Western Cape Government Department of Social Development , 2024',
    },
    {
        name: 'Mobility',
        description: 'Transport infrastructure and services',
        icon: Car,
        color: 'cyan',
        bgColor: 'bg-cyan-1',
        progressBarColor: 'cyan',
        kpis: [
            { name: 'Road maintenance targets achieved', value: 91, target: 90, status: 'Excellent', unit: '%', tranding: 'up' },
            { name: 'Public transport reliability', value: 85, target: 83, status: 'Excellent', unit: '%', tranding: 'up' },
            { name: 'Traffic incident reduction', value: 6.2, target: 5, status: 'Good', unit: '%', tranding: 'up' },
        ],
        source: 'Western Cape Mobility Department, 2024',
    },
    {
        name: 'Infrastructure',
        description: 'Infrastructure development and maintenance',
        icon: Building,
        color: 'info',
        bgColor: 'bg-info-1',
        progressBarColor: 'info',
        kpis: [
            { name: 'Projects completed on time', value: 88, target: 85, status: 'Excellent', tranding: 'up', unit: '%' },
            { name: 'Maintenance contracts meeting standards', value: 92, target: 90, status: 'Excellent', tranding: '', unit: '%' },
            { name: 'Priority infrastructure budget spent', value: 94, target: 95, status: 'Good', tranding: '', unit: '%' },
        ],
        source: 'Western Cape Government Department of Infrastructure, 2024',
    },
    {
        name: 'Economic Development and Tourism',
        description: 'Economic growth and tourism promotion',
        icon: TrendingUp,
        color: 'cyan',
        bgColor: 'bg-cyan-2',
        progressBarColor: 'cyan',
        kpis: [
            { name: 'Jobs supported through programmes', value: 15450, target: 15000, status: 'Excellent', unit: 'Jobs', tranding: 'up' },
            { name: 'Investment facilitation outcomes', value: 2.3, target: 2, status: 'Excellent', unit: 'R Billions', tranding: 'up' },
            { name: 'Tourism growth rate', value: 12.5, target: 10, status: 'Excellent', unit: '%', tranding: 'up' },
        ],
        source: 'Western Cape Government Department of Economic Development and Tourism, 2024',
    },
    {
        name: 'Environmental Affairs and Development Planning ',
        description: 'Environmental protection and planning',
        icon: Leaf,
        color: 'info',
        bgColor: 'bg-info-2',
        progressBarColor: 'info',
        kpis: [
            { name: 'Land under environmental protection', value: 485000, target: 480000, status: 'Excellent', unit: 'Hectares', tranding: 'up' },
            { name: 'Planning regulation compliance', value: 91, target: 90, status: 'Excellent', tranding: '', unit: '%' },
            { name: 'Water & air quality metrics', value: 88, target: 85, status: 'Excellent', tranding: 'up', unit: '%' },
        ],
        source: 'Western Cape Government Department of Environmental Affairs and Development Planning, 2024',
    },
    {
        name: 'Local Government',
        description: 'Municipal support and coordination',
        icon: MapPin,
        color: 'cyan',
        bgColor: 'bg-cyan-3',
        progressBarColor: 'cyan',
        kpis: [
            { name: 'Municipal performance improvement', value: 23, target: 20, status: 'Excellent', unit: 'Municipalities', tranding: 'up' },
            { name: 'Timely intervention in municipalities', value: 89, target: 85, status: 'Excellent', tranding: 'up', unit: '%' },
            { name: 'Service delivery outcomes', value: 84, target: 82, status: 'Excellent', tranding: 'up', unit: '%' },
        ],
        source: 'Western Cape Government Department of Local Government, 2024',
    },
    {
        name: 'Cultural Affairs and Sport',
        description: 'Cultural heritage and sport development',
        icon: Trophy,
        color: 'info',
        bgColor: 'bg-info-3',
        progressBarColor: 'info',
        kpis: [
            { name: 'Cultural events supported', value: 142, target: 135, status: 'Excellent', unit: 'Events', tranding: 'up' },
            { name: 'Sport participation rate', value: 68, target: 65, status: 'Excellent', tranding: 'up', unit: '%' },
            { name: 'Heritage site programmes delivered', value: 45, target: 42, status: 'Excellent', tranding: 'up', unit: 'Programs' },
        ],
        source: 'Western Cape Government Department of Cultural Affairs and Sport, 2024',
    },
]
